/**
 * ISS-031: drill-down drawer for a single stalled row from the
 * sales_outbox / outbox / cron_failures queues.
 *
 * Read-mode for fleet-admin (metadata + payload preview); action
 * buttons (Retry / Dead-letter) render only for superadmin and only
 * when the row isn't already in a terminal state. cron_failures rows
 * have no actions — they're permanent by definition (PR #522).
 */

import { useState } from 'react';
import { toast } from 'react-toastify';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useRBAC } from '@/contexts/RBACContext';
import {
  retryStalledRow,
  deadLetterStalledRow,
} from '@/lib/pocketSettingsApi';
import type { StalledRow } from '@/types/pocketSettings';
import { DeadLetterConfirmDialog } from './DeadLetterConfirmDialog';

interface Props {
  row: StalledRow | null;
  open: boolean;
  onClose: () => void;
  /** Called after a successful retry/dead-letter so the parent can re-fetch. */
  onMutated?: () => void;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function stateBadge(row: StalledRow) {
  if (row.state === 'dlq') {
    return (
      <Badge variant="secondary" className="text-[10px]">
        Dead-lettered
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px]">
      Pending retry
    </Badge>
  );
}

function rowTitle(row: StalledRow): string {
  if (row.source === 'sales') return `sales_outbox · ${row.endpoint}`;
  if (row.source === 'internal') return `outbox · ${row.task_type}`;
  return `cron_failures · ${row.job_name}`;
}

function MetadataTable({ row }: { row: StalledRow }) {
  const entries: Array<[string, React.ReactNode]> = [
    ['ID', <span className="font-mono text-[11px]">{row.id}</span>],
    ['State', stateBadge(row)],
  ];

  if (row.source === 'sales' || row.source === 'internal') {
    entries.push(
      ['Attempts', <span className="tabular-nums">{row.attempts}</span>],
      ['Created', formatDate(row.created_at)],
      ['Next attempt', formatDate(row.next_attempt_at)],
    );
    if (row.failed_permanently_at) {
      entries.push(['Dead-lettered', formatDate(row.failed_permanently_at)]);
    }
  }

  if (row.source === 'sales') {
    if (row.delivered_at) entries.push(['Delivered', formatDate(row.delivered_at)]);
    if (row.last_error) {
      entries.push([
        'Last error',
        <code className="text-[11px] block max-h-32 overflow-y-auto whitespace-pre-wrap">
          {row.last_error}
        </code>,
      ]);
    }
  }

  if (row.source === 'internal') {
    entries.push(
      ['Task type', <code className="text-[11px]">{row.task_type}</code>],
      ['Target', <code className="text-[11px]">{row.target_table}/{row.target_id}</code>],
    );
    if (row.resolved_at) entries.push(['Resolved', formatDate(row.resolved_at)]);
    if (row.resolution) entries.push(['Resolution', row.resolution]);
  }

  if (row.source === 'cron') {
    entries.push(
      ['Job', <code className="text-[11px]">{row.job_name}</code>],
      ['SQLSTATE', <code className="text-[11px]">{row.sqlstate ?? '—'}</code>],
      ['Raised', formatDate(row.raised_at)],
    );
    if (row.unit_id) {
      entries.push(['Unit', <code className="text-[11px]">{row.unit_id}</code>]);
    }
    if (row.message) {
      entries.push([
        'Message',
        <code className="text-[11px] block max-h-32 overflow-y-auto whitespace-pre-wrap">
          {row.message}
        </code>,
      ]);
    }
    if (row.context) {
      entries.push([
        'Context',
        <code className="text-[11px] block max-h-32 overflow-y-auto whitespace-pre-wrap">
          {row.context}
        </code>,
      ]);
    }
  }

  return (
    <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2 text-sm">
      {entries.map(([label, value], i) => (
        <div key={i} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function PayloadPreview({ preview }: { preview: string | null | undefined }) {
  if (!preview) return null;
  return (
    <div className="space-y-1.5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Payload preview
      </h3>
      <pre className="text-[11px] font-mono bg-muted/30 border border-border rounded-md p-3 max-h-64 overflow-auto whitespace-pre-wrap break-all">
        {preview}
      </pre>
    </div>
  );
}

export function StalledRowDrawer({ row, open, onClose, onMutated }: Props) {
  const { isSuperadmin } = useRBAC();
  const [pending, setPending] = useState<'retry' | 'dlq' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!row) {
    return (
      <AdminFormDrawer open={open} onClose={onClose} title="Loading…">
        <p className="text-sm text-muted-foreground">Loading row…</p>
      </AdminFormDrawer>
    );
  }

  const isCron = row.source === 'cron';
  const isTerminal =
    row.state === 'dlq' ||
    (row.source === 'sales' && row.delivered_at != null) ||
    (row.source === 'internal' && row.resolved_at != null);
  const showActions = isSuperadmin && !isCron && !isTerminal;
  const payloadPreview =
    row.source === 'sales' || row.source === 'internal' ? row.payload_preview : null;

  async function onRetry() {
    if (row!.source === 'cron') return;
    setPending('retry');
    try {
      await retryStalledRow(row!.source, row!.id);
      toast.success('Retry scheduled — next drain tick will pick it up.');
      onMutated?.();
      onClose();
    } catch (e) {
      toast.error((e as Error).message || 'Retry failed.');
    } finally {
      setPending(null);
    }
  }

  async function onDeadLetterConfirm(reason: string) {
    if (row!.source === 'cron') return;
    setPending('dlq');
    try {
      await deadLetterStalledRow(row!.source, row!.id, reason || undefined);
      toast.success('Row dead-lettered.');
      onMutated?.();
      setConfirmOpen(false);
      onClose();
    } catch (e) {
      toast.error((e as Error).message || 'Dead-letter failed.');
    } finally {
      setPending(null);
    }
  }

  return (
    <>
      <AdminFormDrawer
        open={open}
        onClose={onClose}
        title={rowTitle(row)}
        description={isCron ? 'Permanent cron failure (no retry semantics).' : undefined}
      >
        <div className="space-y-6">
          <MetadataTable row={row} />
          <PayloadPreview preview={payloadPreview} />

          {showActions && (
            <div className="border-t border-border pt-4 space-y-2">
              <p className="text-xs text-muted-foreground">
                Retry resets <code>next_attempt_at</code> so the next drain tick processes
                this row. Dead-letter sets <code>failed_permanently_at</code> and stops all
                retries.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pending !== null}
                  onClick={onRetry}
                >
                  {pending === 'retry' ? 'Retrying…' : 'Retry now'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={pending !== null}
                  onClick={() => setConfirmOpen(true)}
                >
                  Dead-letter
                </Button>
              </div>
            </div>
          )}

          {isTerminal && !isCron && (
            <p className="text-xs text-muted-foreground border-t border-border pt-4">
              This row is in a terminal state. No further actions available.
            </p>
          )}
        </div>
      </AdminFormDrawer>

      {!isCron && (
        <DeadLetterConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onConfirm={onDeadLetterConfirm}
          submitting={pending === 'dlq'}
        />
      )}
    </>
  );
}
